/**
 * /portal/help — single FAQ + contact CTA (ui-structure.md §Route map).
 *
 * Phase G ships the nav target and a lean, honest FAQ in Eric's
 * client-communication register. Not a knowledge base — one short page that
 * answers the few things people ask and then points at the contact form.
 */

import { createFileRoute, Link } from '@tanstack/react-router';

export const Route = createFileRoute('/portal/help')({
  component: HelpPage,
});

const FAQ: ReadonlyArray<{ q: string; a: string }> = [
  {
    q: 'How fast do tasks come back?',
    a: 'Studio runs on a 72-hour cycle, Pro on 48. The clock on each active task shows exactly where it is.',
  },
  {
    q: 'How many tasks can be active at once?',
    a: 'One at a time on Studio, two on Pro. Everything else waits in the backlog in the order you set.',
  },
  {
    q: 'Can I change the order of my backlog?',
    a: 'Yes. Drag a backlog card, or use the up and down controls on it. Whatever is on top gets pulled next.',
  },
  {
    q: 'What if a deliverable is not quite right?',
    a: 'Open the task and use "Request a revision". Tell me what to change and why; I pick it back up.',
  },
  {
    q: 'How do I pause or cancel?',
    a: 'Settings has both. Pausing keeps your context doc, playbook, and backlog exactly as-is. No retention games.',
  },
];

function HelpPage() {
  return (
    <div className="max-w-2xl space-y-8">
      <div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--color-text-mute)]">
          Help
        </p>
        <h1 className="mb-1 font-display text-h2 italic leading-tight text-[color:var(--color-navy)]">
          The short version.
        </h1>
        <p className="text-sm text-[color:var(--color-text-mute)]">
          If your question is not here, it is worth an email.
        </p>
      </div>
      <dl className="space-y-6">
        {FAQ.map((item) => (
          <div key={item.q}>
            <dt className="mb-1 font-medium text-[color:var(--color-navy)]">
              {item.q}
            </dt>
            <dd className="text-sm leading-relaxed text-[color:var(--color-text-mute)]">
              {item.a}
            </dd>
          </div>
        ))}
      </dl>
      <p className="text-sm text-[color:var(--color-navy)]">
        Still stuck? Use the{' '}
        <a
          href="https://yesplease.studio/contact"
          className="font-medium underline underline-offset-4"
        >
          contact form
        </a>{' '}
        and I will help directly.
      </p>
      <p className="text-sm text-[color:var(--color-text-mute)]">
        Billing questions go through{' '}
        <Link
          to="/portal/settings"
          className="font-medium underline underline-offset-4"
        >
          Settings
        </Link>
        .
      </p>
    </div>
  );
}
